import React from "react";
import {useEditor, EditorContent, Editor} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import {useDebouncedEffect} from "../utils/utils";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ImageIcon from "@mui/icons-material/Image";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";

const EXTENSIONS = [
    StarterKit.configure({
        underline: false,
        link: {
            openOnClick: false,
            HTMLAttributes: {rel: "noopener noreferrer nofollow", target: "_blank"},
            protocols: ["http", "https", "mailto"],
        },
    }),
    Underline,
    Image,
];

type BlockType = "paragraph" | "h1" | "h2" | "h3" | "codeBlock";

const getBlockType = (editor: Editor): BlockType => {
    if (editor.isActive("heading", {level: 1})) return "h1";
    if (editor.isActive("heading", {level: 2})) return "h2";
    if (editor.isActive("heading", {level: 3})) return "h3";
    if (editor.isActive("codeBlock")) return "codeBlock";
    return "paragraph";
};

interface ToolbarButtonProps {
    onClick: () => void;
    active?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title: string;
}

const ToolbarButton = ({onClick, active, disabled, children, title}: ToolbarButtonProps) => (
    <IconButton
        size="small"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
        disabled={disabled}
        title={title}
        sx={{
            borderRadius: 1,
            color: active ? "primary.main" : "action.active",
            backgroundColor: active ? "action.selected" : "transparent",
        }}
    >
        {children}
    </IconButton>
);

interface ToolbarProps {
    editor: Editor | null;
}

const isAllowedUrl = (url: string, allowedProtocols: string[] = ["http:", "https:", "mailto:"]): boolean => {
    try {
        const parsed = new URL(url, window.location.href);
        return allowedProtocols.includes(parsed.protocol);
    } catch {
        return false;
    }
};

const Toolbar = ({editor}: ToolbarProps) => {
    if (!editor) return null;

    const blockType = getBlockType(editor);

    const handleBlockChange = (e: SelectChangeEvent) => {
        const val = e.target.value as BlockType;
        if (val === "paragraph") editor.chain().focus().setParagraph().run();
        else if (val === "h1") editor.chain().focus().toggleHeading({level: 1}).run();
        else if (val === "h2") editor.chain().focus().toggleHeading({level: 2}).run();
        else if (val === "h3") editor.chain().focus().toggleHeading({level: 3}).run();
        else if (val === "codeBlock") editor.chain().focus().toggleCodeBlock().run();
    };

    const insertLink = () => {
        const url = window.prompt("Enter URL:");
        if (url && isAllowedUrl(url)) {
            editor.chain().focus().setLink({href: url}).run();
        }
    };

    const insertImage = () => {
        const url = window.prompt("Enter image URL:");
        if (url && isAllowedUrl(url, ["http:", "https:"])) {
            editor.chain().focus().setImage({src: url}).run();
        }
    };

    return (
        <div className="editorToolbar" style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px", padding: "4px"}}>
            <Select
                size="small"
                value={blockType}
                onChange={handleBlockChange}
                variant="standard"
                sx={{minWidth: 120, mr: 1, fontSize: "0.875rem"}}
            >
                <MenuItem value="paragraph">Normal</MenuItem>
                <MenuItem value="h1">Heading Large</MenuItem>
                <MenuItem value="h2">Heading Medium</MenuItem>
                <MenuItem value="h3">Heading Small</MenuItem>
                <MenuItem value="codeBlock">Code Block</MenuItem>
            </Select>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />

            <ToolbarButton
                title="Bold"
                active={editor.isActive("bold")}
                onClick={() => editor.chain().focus().toggleBold().run()}
            >
                <FormatBoldIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Italic"
                active={editor.isActive("italic")}
                onClick={() => editor.chain().focus().toggleItalic().run()}
            >
                <FormatItalicIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Underline"
                active={editor.isActive("underline")}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <FormatUnderlinedIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Strikethrough"
                active={editor.isActive("strike")}
                onClick={() => editor.chain().focus().toggleStrike().run()}
            >
                <StrikethroughSIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Code"
                active={editor.isActive("code")}
                onClick={() => editor.chain().focus().toggleCode().run()}
            >
                <CodeIcon fontSize="small" />
            </ToolbarButton>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />

            <ToolbarButton
                title="Bullet List"
                active={editor.isActive("bulletList")}
                onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
                <FormatListBulletedIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Ordered List"
                active={editor.isActive("orderedList")}
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
                <FormatListNumberedIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Blockquote"
                active={editor.isActive("blockquote")}
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
                <FormatQuoteIcon fontSize="small" />
            </ToolbarButton>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />

            <ToolbarButton
                title="Insert Link"
                active={editor.isActive("link")}
                onClick={insertLink}
            >
                <LinkIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Remove Link"
                onClick={() => editor.chain().focus().unsetLink().run()}
                disabled={!editor.isActive("link")}
            >
                <LinkOffIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Insert Image"
                onClick={insertImage}
            >
                <ImageIcon fontSize="small" />
            </ToolbarButton>

            <Divider orientation="vertical" flexItem sx={{mx: 0.5}} />

            <ToolbarButton
                title="Undo"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
            >
                <UndoIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton
                title="Redo"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
            >
                <RedoIcon fontSize="small" />
            </ToolbarButton>
        </div>
    );
};

interface EnhancedRteProps {
    disabled?: boolean;
    value?: string;
    attribute: {Name: string; Type?: string};
    onChange: (name: string, value: string | undefined) => void;
    [key: string]: any;
}

export const EnhancedRte = ({disabled, value, attribute, onChange}: EnhancedRteProps) => {
    const [html, setHtml] = React.useState(value || "");

    const editor = useEditor({
        extensions: EXTENSIONS,
        content: value || "",
        editable: !disabled,
        onUpdate: ({editor}) => {
            setHtml(editor.getHTML());
        },
    });

    React.useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    React.useEffect(() => {
        if (!editor) return;
        const currentHtml = editor.getHTML();
        if (value === undefined && !editor.isEmpty) {
            editor.commands.clearContent(false);
            setHtml("");
        } else if (value !== undefined && value !== currentHtml) {
            editor.commands.setContent(value, {emitUpdate: false});
            setHtml(value);
        }
    }, [value, editor]);

    useDebouncedEffect(() => {
        if (!editor) return;
        try {
            if (value === html) return;
            if (editor.isEmpty) {
                onChange(attribute.Name, undefined);
                return;
            }
            onChange(attribute.Name, html);
        } catch (e) {
            console.error(e);
        }
    }, [html], 500);

    return (
        <div className="textEditor" style={{width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden"}}>
            <Toolbar editor={editor} />
            <EditorContent editor={editor} style={{flex: 1, overflow: "auto"}} />
        </div>
    );
};

interface FormHelperEnhancedRteProps {
    input: {name: string; value: string; onChange: (value: any) => void; [key: string]: any};
    meta: {submitError?: any; dirtySinceLastSubmit?: boolean; error?: any; touched?: boolean};
    label?: string;
    formControlProps?: object;
    shrink?: boolean;
    required?: boolean;
    [key: string]: any;
}

export const FormHelperEnhancedRte = ({
    input: {name, value, onChange, ...restInput},
    meta,
    label,
    formControlProps,
    shrink,
    ...rest
}: FormHelperEnhancedRteProps) => {
    const showError = ((meta.submitError && !meta.dirtySinceLastSubmit) || meta.error) && meta.touched;

    return (
        <FormControl {...formControlProps} error={showError}>
            {label &&
                <InputLabel shrink={shrink ? true : undefined} htmlFor={name}
                    style={{
                        position: "relative",
                        transform: "none",
                        marginBottom: "4px"
                    }} required={rest.required}
                >
                    {label}
                </InputLabel>
            }

            <EnhancedRte
                {...rest}
                {...restInput}
                value={value}
                label={label}
                error={showError ? meta.error || meta.submitError : ""}
                attribute={{Name: name, Type: "RTE"}}
                onChange={(_name: string, value: string | undefined) => onChange(value)}
            />

            {showError && ((meta.error && meta.error !== true) || (meta.submitError && meta.submitError !== true)) &&
                <FormHelperText>{meta.error || meta.submitError}</FormHelperText>
            }
        </FormControl>
    );
};

interface HtmlPreviewProps {
    value?: string;
    className?: string;
}

// Read-only, toolbar-less render of HTML content. Uses the same schema as the
// editors so content displays as it would in an HTML input, and anything the
// schema doesn't allow (scripts, event handlers) is dropped.
export const HtmlPreview = ({value, className}: HtmlPreviewProps) => {
    const editor = useEditor({
        extensions: EXTENSIONS,
        content: value || "",
        editable: false,
    });

    React.useEffect(() => {
        if (!editor) return;
        if ((value || "") !== editor.getHTML()) {
            editor.commands.setContent(value || "", {emitUpdate: false});
        }
    }, [value, editor]);

    return <EditorContent editor={editor} className={className || "htmlPreview"} />;
};

interface SimpleRteProps {
    value?: string;
    onChange: (html: string, isEmpty: boolean) => void;
    disabled?: boolean;
    className?: string;
}

export const SimpleRte = ({value, onChange, disabled, className}: SimpleRteProps) => {
    const editor = useEditor({
        extensions: EXTENSIONS,
        content: value || "",
        editable: !disabled,
        onUpdate: ({editor}) => {
            onChange(editor.getHTML(), editor.isEmpty);
        },
    });

    React.useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    React.useEffect(() => {
        if (!editor) return;
        if (value === "" && editor.isEmpty) return;
        const currentHtml = editor.getHTML();
        if (value !== undefined && value !== currentHtml) {
            editor.commands.setContent(value, {emitUpdate: false});
        }
    }, [value, editor]);

    return (
        <div className={className || "textEditor"} style={{width: "100%", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden"}}>
            <Toolbar editor={editor} />
            <EditorContent editor={editor} style={{flex: 1, overflow: "auto"}} />
        </div>
    );
};
